import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { vehiclesAPI, alertsAPI, paymentsAPI } from '../services/api';
import VehicleIllustration from '../components/VehicleIllustration';
import ServiceModal from '../components/ServiceModal';
import VehicleModal from '../components/VehicleModal';
import useIsMobile from '../hooks/useIsMobile';
import toast from 'react-hot-toast';

const FREE_VEHICLES = 3;
const EXTRA_VEHICLE_PRICE_LABEL = 'R$ 9,90/mês';

// Formata o gasto real do ano de forma compacta pro card do KPI (que é um espaço pequeno,
// de uma linha só): acima de R$ 1.000 mostra abreviado em milhares ("R$ 1,2k"), abaixo disso
// mostra o valor cheio com centavos ("R$ 285,50"). Mesmo padrão de "nunca inventar decimais,
// sempre formatar igual" do formatBRL em VehicleDashboard.jsx — só que compacto pra caber
// no card pequeno.
function formatCompactBRL(value) {
  const n = Number(value) || 0;
  if (n >= 1000) return `R$ ${(n / 1000).toFixed(1).replace('.', ',')}k`;
  return `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function KpiCard({ icon, value, label, alert, m }) {
  const bgColor = alert ? '#fdece0' : '#e6f0e9';
  const iconColor = alert ? '#e97117' : '#14562e';
  return (
    <div style={{ background: '#fff', border: '1px solid #e5ddc9', borderRadius: '16px', padding: m ? '14px' : '20px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: bgColor, color: iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</div>
      <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, fontSize: m ? '24px' : '32px', color: '#14562e', letterSpacing: '-0.02em', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: m ? '10px' : '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px', color: '#6a7268' }}>{label}</div>
    </div>
  );
}

function VehicleCard({ vehicle, onClick, access, onResolvePayment, m }) {
  const nextServiceKm = 10000 - (vehicle.current_km % 10000);
  const kmProgress = Math.min((vehicle.current_km % 10000) / 10000 * 100, 100);
  const isMotorcycle = vehicle.type === 'motorcycle';
  const emojiBg = isMotorcycle ? '#fdece0' : '#e6f0e9';
  const lastServiceDate = vehicle.last_service_date;
  let lastServiceLabel = 'Sem serviços';
  if (lastServiceDate) {
    const diff = Math.floor((Date.now() - new Date(lastServiceDate).getTime()) / 86400000);
    if (diff === 0) lastServiceLabel = 'hoje';
    else if (diff === 1) lastServiceLabel = 'há 1 dia';
    else lastServiceLabel = `há ${diff} dias`;
  }
  const progressColor = kmProgress > 80 ? 'linear-gradient(90deg, #c94a2f, #e0603a)' : kmProgress > 50 ? 'linear-gradient(90deg, #e97117, #f39a4c)' : 'linear-gradient(90deg, #14562e, #1a6b3a)';

  // Veículo além do limite grátis, sem assinatura ativa/trialing ainda (nunca chegou a
  // completar o Checkout, ou o pagamento falhou). Fica visível mas travado, com um jeito
  // claro de retomar o pagamento — em vez de sumir ou parecer liberado por engano.
  const pending = access === 'view_only';

  return (
    <div onClick={pending ? undefined : onClick} style={{ background: '#fff', border: pending ? '1px solid #f0c896' : '1px solid #e5ddc9', borderRadius: '22px', padding: m ? '18px' : '24px', cursor: pending ? 'default' : 'pointer', transition: 'transform 0.2s, box-shadow 0.2s', display: 'flex', flexDirection: 'column', gap: '14px', opacity: pending ? 0.75 : 1, position: 'relative' }}
      onMouseOver={e => { if (!pending) { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(15,64,35,.09)'; } }}
      onMouseOut={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: emojiBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <VehicleIllustration type={vehicle.type} color={isMotorcycle ? '#e97117' : '#14562e'} size={34} />
        </div>
        {pending ? (
          <span style={{ background: '#fdecd8', padding: '4px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: '#7a3d10' }}>🔒 Pagamento pendente</span>
        ) : vehicle.validated_count > 0 && (
          <span style={{ background: '#ede4d1', padding: '4px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: '#14562e' }}>Verificado</span>
        )}
      </div>
      <div>
        <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, fontSize: m ? '15px' : '20px', color: '#14562e', margin: 0, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
          {vehicle.nickname || `${vehicle.brand} ${vehicle.model}`} {vehicle.year}
        </h3>
        {vehicle.plate && <span style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 700, background: '#111', color: '#fff', padding: '3px 10px', borderRadius: '6px', fontSize: '12px', letterSpacing: '1.5px', display: 'inline-block', marginTop: '6px' }}>{vehicle.plate}</span>}
      </div>

      {pending ? (
        <>
          <p style={{ fontSize: '13px', color: '#7a3d10', lineHeight: 1.5, margin: 0 }}>
            Não conseguimos confirmar a assinatura desse veículo ({EXTRA_VEHICLE_PRICE_LABEL}). Ele fica visível, mas travado até o pagamento ser concluído.
          </p>
          <button
            onClick={e => { e.stopPropagation(); onResolvePayment?.(); }}
            style={{ alignSelf: 'flex-start', padding: '9px 16px', borderRadius: '10px', fontWeight: 700, fontSize: '13px', cursor: 'pointer', border: 'none', background: '#e97117', color: '#fff', fontFamily: "'DM Sans', sans-serif" }}
          >
            Resolver pagamento →
          </button>
        </>
      ) : (
        <>
          <div>
            <div style={{ height: '6px', background: '#f4ede0', borderRadius: '6px', overflow: 'hidden' }}>
              <span style={{ display: 'block', height: '100%', background: progressColor, borderRadius: '6px', width: `${kmProgress}%` }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6a7268', marginTop: '6px' }}>
              <span>Próx. revisão</span>
              <b style={{ color: '#14562e' }}>em {nextServiceKm.toLocaleString('pt-BR')} km</b>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', paddingTop: '12px', borderTop: '1px solid #efe8d6' }}>
            <div>
              <small style={{ display: 'block', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1.2px', color: '#6a7268', fontWeight: 600 }}>Quilometragem</small>
              <b style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: m ? '15px' : '18px', color: '#14562e', fontWeight: 700 }}>{vehicle.current_km.toLocaleString('pt-BR')} km</b>
            </div>
            <div>
              <small style={{ display: 'block', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1.2px', color: '#6a7268', fontWeight: 600 }}>Último serviço</small>
              <b style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: m ? '15px' : '18px', color: '#14562e', fontWeight: 700 }}>{lastServiceLabel}</b>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function AddVehicleCard({ onClick, atLimit, m }) {
  return (
    <div onClick={onClick} style={{ background: 'transparent', border: '2px dashed #e5ddc9', borderRadius: '22px', padding: m ? '24px 18px' : '24px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', minHeight: m ? '180px' : '280px', transition: 'all 0.2s' }}
      onMouseOver={e => { e.currentTarget.style.borderColor = '#14562e'; e.currentTarget.style.background = '#fff'; }}
      onMouseOut={e => { e.currentTarget.style.borderColor = '#e5ddc9'; e.currentTarget.style.background = 'transparent'; }}>
      <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: '#14562e', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px' }}>+</div>
      <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, textTransform: 'uppercase', fontSize: '14px', color: '#14562e' }}>Adicionar veículo</div>
      {atLimit ? (
        <>
          <small style={{ color: '#6a7268', fontSize: '13px', textAlign: 'center', maxWidth: '200px' }}>Seus 3 veículos grátis já estão cadastrados.</small>
          <span style={{ background: '#fdecd8', color: '#7a3d10', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '999px' }}>{EXTRA_VEHICLE_PRICE_LABEL} a partir do 4º</span>
        </>
      ) : (
        <small style={{ color: '#6a7268', fontSize: '13px', textAlign: 'center', maxWidth: '200px' }}>Carro, moto, pickup ou SUV. Os dados da FIPE vêm automaticamente.</small>
      )}
    </div>
  );
}

/* [FIX] Aviso exibido antes de abrir o cadastro quando os 3 veículos grátis já foram
   usados. Antes dizia que o veículo era liberado na hora mas a cobrança só acontecia
   30 dias depois (trial) — decisão de produto foi tirar essa carência: agora deixa
   claro que o pagamento é cobrado no ato, o veículo libera assim que confirma, e a
   assinatura é mensal recorrente, cancelável a qualquer momento sem custo (ver
   POST /payments/cancel, que já cancela sem multa — isso não mudou). */
function UpsellModal({ open, onCancel, onConfirm }) {
  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,64,35,.4)', backdropFilter: 'blur(6px)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}>
      <div style={{ background: '#fff', borderRadius: '22px', padding: '32px', maxWidth: '460px', width: '100%', boxShadow: '0 20px 48px rgba(15,64,35,.14)' }}>
        <h2 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, textTransform: 'uppercase', fontSize: '20px', margin: 0, color: '#14562e' }}>
          Você já usou seus 3 veículos grátis 🚗
        </h2>
        <p style={{ color: '#183d24', fontSize: '14.5px', lineHeight: 1.6, marginTop: '14px' }}>
          Os 3 primeiros veículos do NICO são <b>gratuitos para sempre</b>. A partir do próximo, cada veículo adicional custa <b>{EXTRA_VEHICLE_PRICE_LABEL}</b>.
        </p>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', background: '#e6f0e9', border: '1px solid rgba(20,86,46,0.15)', borderRadius: '12px', padding: '14px', marginTop: '14px' }}>
          <span style={{ fontSize: '18px', lineHeight: 1 }}>✅</span>
          <span style={{ fontSize: '13px', color: '#14562e', lineHeight: 1.5 }}>
            Ao confirmar o pagamento, seu veículo é liberado <b>na hora</b>. Cobrança mensal recorrente — cancele quando quiser, sem custo.
          </span>
        </div>
        <div style={{ display: 'flex', gap: '10px', marginTop: '22px', justifyContent: 'flex-end' }}>
          <button style={{ padding: '12px 20px', borderRadius: '12px', fontWeight: 700, fontSize: '14px', cursor: 'pointer', border: 'none', background: 'transparent', color: '#183d24', fontFamily: "'DM Sans', sans-serif" }}
            onClick={onCancel}>Agora não</button>
          <button style={{ padding: '12px 20px', borderRadius: '12px', fontWeight: 700, fontSize: '14px', cursor: 'pointer', border: 'none', background: '#14562e', color: '#fff', fontFamily: "'DM Sans', sans-serif" }}
            onClick={onConfirm}>Cadastrar veículo adicional</button>
        </div>
      </div>
    </div>
  );
}

export default function GaragePage() {
  const [vehicles, setVehicles] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [accessMap, setAccessMap] = useState({}); // { [vehicle_id]: 'free' | 'paid' | 'view_only' }
  const [loading, setLoading] = useState(true);
  const [serviceModalOpen, setServiceModalOpen] = useState(false);
  const [vehicleModalOpen, setVehicleModalOpen] = useState(false);
  const [upsellOpen, setUpsellOpen] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  // [NEW] Aba "Veículos vendidos" — histórico só-leitura do que o usuário já repassou
  // pra outra conta (ver GET /vehicles/vendidos em vehicles.js).
  const [soldVehicles, setSoldVehicles] = useState([]);
  const [tab, setTab] = useState('active'); // 'active' | 'sold'
  // [FIX 01/set/2026] Gasto real do ano corrente, somado no backend (GET /vehicles agora
  // devolve `year_cost`) — antes o card de gasto era um valor inventado, não vinha da API.
  const [yearCost, setYearCost] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();
  const m = useIsMobile();

  const loadData = () => {
    Promise.all([
      vehiclesAPI.list()
        .then(r => ({ vehicles: r.vehicles || r.data?.vehicles || [], yearCost: Number(r.year_cost ?? r.data?.year_cost) || 0 }))
        .catch(() => ({ vehicles: [], yearCost: 0 })),
      alertsAPI.list().then(r => r.alerts || r.data?.alerts || []).catch(() => []),
      // Diz, veículo a veículo, se está 'free', 'paid' (assinatura ativa/trialing) ou
      // 'view_only' (passou do limite grátis e ainda não tem assinatura confirmada).
      paymentsAPI.vehicleAccess().then(r => r.access || []).catch(() => []),
      vehiclesAPI.sold().then(r => r.vehicles || r.data?.vehicles || []).catch(() => []),
    ]).then(([vData, a, access, sold]) => {
      setVehicles(vData.vehicles);
      setYearCost(vData.yearCost);
      setAlerts(a);
      const map = {};
      for (const entry of access) map[entry.vehicle_id] = entry.access;
      setAccessMap(map);
      setSoldVehicles(sold);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { loadData(); }, []);

  const validatedServices = vehicles.reduce((sum, v) => sum + (parseInt(v.validated_count) || 0), 0);
  const activeAlerts = Array.isArray(alerts) ? alerts.filter(a => a.is_active).length : 0;
  const atFreeLimit = vehicles.length >= FREE_VEHICLES;
  const firstName = user?.name?.split(' ')[0] || 'Usuário';

  // Ponto único de entrada pro cadastro: até o 3º veículo, abre o modal direto.
  // Do 4º em diante, mostra antes o aviso de cobrança (pagamento na hora, liberado na
  // hora, sem carência — ver UpsellModal acima).
  const handleAddClick = () => {
    if (atFreeLimit) setUpsellOpen(true);
    else setVehicleModalOpen(true);
  };

  const handleUpsellConfirm = () => {
    setUpsellOpen(false);
    setVehicleModalOpen(true);
  };

  // Dispara o Checkout do Stripe pra um veículo específico e redireciona.
  // Usado tanto logo após o cadastro quanto no botão "Resolver pagamento" de um
  // veículo que ficou travado (checkout anterior falhou, foi fechado, ou nunca rodou).
  // Se der erro aqui, o veículo simplesmente continua 'view_only' — o backend só marca
  // 'active'/'trialing' quando o Checkout é realmente concluído (webhook), então não tem
  // como ele "escapar" travado por engano.
  const startCheckout = async (vehicleId) => {
    setCheckingOut(true);
    try {
      const { url } = await paymentsAPI.createCheckout(vehicleId);
      if (url) {
        window.location.href = url;
      } else {
        toast.error('Não foi possível iniciar o pagamento. Tente novamente na garagem.');
      }
    } catch (err) {
      toast.error(err.message || 'Erro ao iniciar a assinatura do veículo adicional');
    } finally {
      setCheckingOut(false);
      loadData(); // garante que o card aparece como "Pagamento pendente" se algo falhou
    }
  };

  // Chamado quando o VehicleModal termina de criar o veículo (recebe o id do veículo criado).
  // [FIX] Se esse veículo passou do limite gratuito, já dispara o Checkout do Stripe —
  // sem trial: o cartão é cobrado no ato, e o veículo só libera quando o backend
  // confirmar isso pelo webhook (ver comentário em payments.js).
  const handleVehicleCreated = async (newVehicleId) => {
    const wasExtra = atFreeLimit; // limite calculado ANTES desse veículo entrar na lista
    loadData();
    if (!wasExtra || !newVehicleId) return;
    startCheckout(newVehicleId);
  };

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', flexDirection: 'column', gap: '12px' }}>
      <div className="spinner" style={{ width: '32px', height: '32px' }} />
    </div>
  );

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: m ? 'flex-start' : 'flex-end', gap: '16px', marginBottom: '20px', flexDirection: m ? 'column' : 'row' }}>
        <div>
          <span style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.8px', color: '#e97117' }}>Sua garagem</span>
          <h1 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, textTransform: 'uppercase', fontSize: m ? '26px' : '44px', lineHeight: 0.95, letterSpacing: '-0.02em', margin: 0, color: '#14562e', wordBreak: 'break-word' }}>
            Oi, {firstName} 👋
          </h1>
          <p style={{ color: '#6a7268', margin: '6px 0 0', fontSize: '14px' }}>
            {vehicles.length === 0 ? 'Adicione seu primeiro veículo!' : 'Bem-vindo de volta. Seus veículos estão em dia.'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', width: m ? '100%' : 'auto' }}>
          <button style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: m ? '10px 14px' : '12px 22px', borderRadius: '12px', fontWeight: 700, fontSize: '13px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", background: 'transparent', color: '#14562e', border: '1.5px solid #14562e', flex: m ? 1 : 'none' }}
            onClick={() => setServiceModalOpen(true)}>+ Novo serviço</button>
          <button style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: m ? '10px 14px' : '12px 22px', borderRadius: '12px', fontWeight: 700, fontSize: '13px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif", background: '#14562e', color: '#fff', border: 'none', flex: m ? 1 : 'none' }}
            onClick={handleAddClick}>+ Adicionar</button>
        </div>
      </div>

      {/* [NEW] Abas Meus veículos / Veículos vendidos — só aparece pra quem já repassou
          algum veículo, pra não poluir a garagem de quem nunca usou a feature. */}
      {soldVehicles.length > 0 && (
        <div style={{ display: 'flex', gap: '6px', marginBottom: '20px', borderBottom: '1px solid #e5ddc9' }}>
          {[['active', `Meus veículos · ${vehicles.length}`], ['sold', `Veículos vendidos · ${soldVehicles.length}`]].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)}
              style={{ padding: '10px 4px', marginRight: '18px', background: 'none', border: 'none', borderBottom: tab === key ? '2px solid #14562e' : '2px solid transparent', color: tab === key ? '#14562e' : '#6a7268', fontWeight: 700, fontSize: '13.5px', cursor: 'pointer', fontFamily: "'DM Sans', sans-serif" }}>
              {label}
            </button>
          ))}
        </div>
      )}

      {tab === 'sold' ? (
        /* [NEW] Veículos vendidos — só leitura, mantém o histórico que o usuário mesmo
            registrou até a data do repasse (ver GET /vehicles/vendidos e /vehicles/:id). */
        soldVehicles.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 20px' }}>
            <div style={{ fontSize: '44px', opacity: 0.4, marginBottom: '16px' }}>👋</div>
            <p style={{ color: '#6a7268', fontSize: '14px' }}>Nenhum veículo repassado ainda</p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: m ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
            {soldVehicles.map(v => (
              <div key={v.id} onClick={() => navigate(`/garagem/${v.id}`)}
                style={{ background: '#fff', border: '1px solid #e5ddc9', borderRadius: '22px', padding: m ? '18px' : '24px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: '14px', opacity: 0.85 }}
                onMouseOver={e => { e.currentTarget.style.opacity = '1'; }}
                onMouseOut={e => { e.currentTarget.style.opacity = '0.85'; }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ width: '52px', height: '52px', borderRadius: '14px', background: '#f4ede0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <VehicleIllustration type={v.type} color="#6a7268" size={34} />
                  </div>
                  <span style={{ background: 'rgba(20,86,46,0.1)', padding: '4px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: '#14562e' }}>👁️ Vendido</span>
                </div>
                <div>
                  <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, fontSize: m ? '15px' : '20px', color: '#14562e', margin: 0, wordBreak: 'break-word' }}>
                    {v.nickname || `${v.brand} ${v.model}`} {v.year}
                  </h3>
                  {v.plate && <span style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 700, background: '#111', color: '#fff', padding: '3px 10px', borderRadius: '6px', fontSize: '12px', letterSpacing: '1.5px', display: 'inline-block', marginTop: '6px' }}>{v.plate}</span>}
                </div>
                <p style={{ fontSize: '13px', color: '#6a7268', margin: 0 }}>
                  Repassado {v.sold_at ? `em ${new Date(v.sold_at).toLocaleDateString('pt-BR')}` : ''}
                </p>
              </div>
            ))}
          </div>
        )
      ) : (
        <>
          {/* KPIs */}
          <div style={{ display: 'grid', gridTemplateColumns: m ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: m ? '10px' : '16px', marginBottom: '24px' }}>
            <KpiCard m={m} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2"/><circle cx="6.5" cy="16.5" r="2.5"/><circle cx="16.5" cy="16.5" r="2.5"/></svg>} value={vehicles.length} label="veículos" />
            <KpiCard m={m} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>} value={validatedServices} label="serviços validados" />
            <KpiCard m={m} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/></svg>} value={activeAlerts} label="alertas ativos" alert />
            <KpiCard m={m} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>} value={formatCompactBRL(yearCost)} label={`gasto em ${new Date().getFullYear()}`} />
          </div>

          {/* Vehicle Grid */}
          {vehicles.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px 20px' }}>
              <div style={{ fontSize: '44px', opacity: 0.4, marginBottom: '16px' }}>🚗</div>
              <h3 style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, fontSize: '18px', color: '#14562e' }}>Sua garagem está vazia</h3>
              <p style={{ color: '#6a7268', fontSize: '14px', marginTop: '4px' }}>Adicione seu primeiro veículo</p>
              <button style={{ marginTop: '16px', padding: '12px 22px', borderRadius: '12px', background: '#14562e', color: '#fff', fontWeight: 700, fontSize: '14px', cursor: 'pointer', border: 'none', fontFamily: "'DM Sans', sans-serif" }}
                onClick={handleAddClick}>+ Adicionar veículo</button>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: m ? '1fr' : 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
              {vehicles.map(v => (
                <VehicleCard
                  key={v.id}
                  vehicle={v}
                  access={accessMap[v.id]}
                  onClick={() => navigate(`/garagem/${v.id}`)}
                  onResolvePayment={() => startCheckout(v.id)}
                  m={m}
                />
              ))}
              <AddVehicleCard onClick={handleAddClick} atLimit={atFreeLimit} m={m} />
            </div>
          )}
        </>
      )}

      {checkingOut && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,64,35,.4)', backdropFilter: 'blur(6px)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px' }}>
          <div className="spinner" style={{ width: '32px', height: '32px', borderTopColor: '#fff' }} />
          <span style={{ color: '#fff', fontWeight: 600, fontSize: '14px' }}>Preparando o pagamento…</span>
        </div>
      )}

      <UpsellModal open={upsellOpen} onCancel={() => setUpsellOpen(false)} onConfirm={handleUpsellConfirm} />
      <ServiceModal open={serviceModalOpen} onClose={() => setServiceModalOpen(false)} vehicles={vehicles} onSuccess={loadData} />
      <VehicleModal open={vehicleModalOpen} onClose={() => setVehicleModalOpen(false)} onSuccess={handleVehicleCreated} />
    </div>
  );
}
